import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMapGL, { MapRef, ViewState } from "react-map-gl";
import { useTranslation } from "react-i18next";
import styles from "./ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import ImageFinder from "../../../../share/ImageFinder/ImageFinder";
import Details from "./ActiveTabs/Details";
import Contacts from "./ActiveTabs/Contacts";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { ICurrentMarker } from "../../../../models/CurrentMarker";
import { MapObject } from "../../../../models/MapObject";
import {
  mapboxAccessToken,
  defaultViewport,
} from "../../../../constants/mapConstants";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import util from "../../../../util";

interface ListViewObjectDetailsProps {
  selectedMapObject: Partial<MapObject>;
  isDetailsActive: boolean;
  onClose: () => void;
}

const ListViewObjectDetails: React.FC<ListViewObjectDetailsProps> = ({
  selectedMapObject,
  isDetailsActive,
  onClose,
}) => {
  const { config } = useTypedSelector((state) => state.mapObjectsInfo);
  const selectedLanguage = useSelectedLanguage();
  const { t: tObjectDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails",
  });

  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>(() => {
    const initialLat = Number(selectedMapObject?.lat);
    const initialLon = Number(selectedMapObject?.lon);
    const hasInitialCoordinates =
      Number.isFinite(initialLat) && Number.isFinite(initialLon);

    return {
      latitude: hasInitialCoordinates ? initialLat : defaultViewport.latitude,
      longitude: hasInitialCoordinates ? initialLon : defaultViewport.longitude,
      zoom: hasInitialCoordinates ? 13 : defaultViewport.zoom,
      bearing: 0,
      pitch: 0,
    };
  });

  useEffect(() => {
    const rawLat = Number(selectedMapObject?.lat);
    const rawLon = Number(selectedMapObject?.lon);
    const hasCoordinates = Number.isFinite(rawLat) && Number.isFinite(rawLon);
    const latitude = hasCoordinates ? rawLat : defaultViewport.latitude;
    const longitude = hasCoordinates ? rawLon : defaultViewport.longitude;

    setViewState((prev) => ({
      ...prev,
      latitude,
      longitude,
      zoom: hasCoordinates ? 13 : defaultViewport.zoom,
    }));
  }, [selectedMapObject?.lat, selectedMapObject?.lon]);

  const marker = useMemo<ICurrentMarker>(() => {
    const lat = Number(selectedMapObject?.lat);
    const lon = Number(selectedMapObject?.lon);

    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }

    return null;
  }, [selectedMapObject?.lat, selectedMapObject?.lon]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (map) {
      util.mapInterfaceUtil.changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const description =
    selectedMapObject.formatted_address || selectedMapObject.address || "";
  const title = selectedMapObject.aimap_classifier || "";

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={<ImageFinder object={selectedMapObject} />}
      />
      <div className={styles.listViewDetailsLayout}>
        <div className={styles.listViewMapWrapper}>
          <ReactMapGL
            {...viewState}
            ref={(instance) => (mapRef.current = instance)}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={config?.mapStyle}
            onMove={(event) => setViewState(event.viewState)}
            dragRotate={false}
            doubleClickZoom={false}
            scrollZoom={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
            onLoad={handleMapLoad}
          >
            {marker && <CustomMarker marker={marker} />}
          </ReactMapGL>
        </div>
        <div className={styles.listViewPanels}>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("details.title")}
            </h3>
            <Details
              key={`details-${
                selectedMapObject.hash_id || selectedMapObject.name
              }`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("contacts.title")}
            </h3>
            <Contacts
              key={`contacts-${
                selectedMapObject.hash_id || selectedMapObject.name
              }`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
        </div>
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewObjectDetails;
